import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Clock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useLengthOfStay } from "@/hooks/use-population-insights";
import type { LosGroupData } from "@/hooks/use-population-insights";
import { useInsightsContext } from "@/contexts/insights-context";
import { useState, useMemo } from "react";

type SpeciesFilter = "all" | "dogs" | "cats" | "other";

const SPECIES_API_MAP: Record<SpeciesFilter, string | undefined> = {
  all: undefined,
  dogs: "DOG",
  cats: "CAT",
  other: undefined,
};

interface LengthOfStayChartProps {
  days: number;
}

function StatCard({ label, shortLabel, value, sub }: { label: string; shortLabel?: string; value: string | number; sub?: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md border" data-testid={`stat-los-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="text-xs text-muted-foreground">
        {shortLabel ? <><span className="sm:hidden">{shortLabel}</span><span className="hidden sm:inline">{label}</span></> : label}
      </span>
      <span className="text-lg font-semibold">{value}</span>
      {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </div>
  );
}

function LosBarChart({ data, title, testId }: { data: LosGroupData; title: string; testId: string }) {
  if (data.totalAnimals === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm" data-testid={`empty-${testId}`}>
        No {title.toLowerCase()} data available
      </div>
    );
  }

  const chartData = data.buckets.map((b) => ({
    label: b.label,
    count: b.count,
    pct: data.totalAnimals > 0 ? Math.round((b.count / data.totalAnimals) * 100) : 0,
  }));

  return (
    <div data-testid={testId}>
      <div className="mb-4">
        <p className="text-xs text-muted-foreground mb-1" data-testid="text-los-stats-hint">
          All records within selected date range
        </p>
        <div className="flex flex-wrap gap-3">
          <StatCard label="Average" value={`${data.averageDays} days`} />
          <StatCard label="Median" value={`${data.medianDays} days`} />
          <StatCard label="Total Animals" shortLabel="Total" value={data.totalAnimals} />
        </div>
      </div>
      <div className="h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" />
            <YAxis type="category" dataKey="label" width={80} tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value: number, _name: string, props: any) => [
                `${value} animals (${props.payload.pct}%)`,
                title,
              ]}
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                borderColor: "hsl(var(--border))",
                borderRadius: "6px",
                color: "hsl(var(--card-foreground))",
              }}
            />
            <Bar dataKey="count" fill={title === "Outcomed" ? "hsl(var(--chart-1))" : "hsl(var(--chart-2))"} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function LengthOfStayChart({ days }: LengthOfStayChartProps) {
  const { selectedShelter, isSystemView } = useInsightsContext();
  const location = isSystemView ? undefined : selectedShelter;
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");

  const apiSpecies = SPECIES_API_MAP[speciesFilter];
  const { data, isLoading, isError, refetch } = useLengthOfStay(days, location, apiSpecies);

  const outcomedData = useMemo(() => {
    if (!data) return null;
    if (speciesFilter === "all") return data.outcomed.all;
    if (speciesFilter === "other" && data.outcomed.bySpecies?.OTHER) return data.outcomed.bySpecies.OTHER;
    return data.outcomed.all;
  }, [data, speciesFilter]);

  const currentData = useMemo(() => {
    if (!data) return null;
    if (speciesFilter === "all") return data.currentInShelter.all;
    if (speciesFilter === "other" && data.currentInShelter.bySpecies?.OTHER) return data.currentInShelter.bySpecies.OTHER;
    return data.currentInShelter.all;
  }, [data, speciesFilter]);

  return (
    <Card data-testid="card-length-of-stay">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Length of Stay
          </CardTitle>
        </div>
        <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
          <SelectTrigger className="w-[140px]" data-testid="select-species-los">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Species</SelectItem>
            <SelectItem value="dogs">Dogs</SelectItem>
            <SelectItem value="cats">Cats</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-4" data-testid="loading-los">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-[250px] w-full" />
          </div>
        )}
        {isError && (
          <Alert variant="destructive" data-testid="error-los">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Failed to load length-of-stay data</span>
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-los">
                <RefreshCw className="h-3 w-3 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {outcomedData && currentData && outcomedData.totalAnimals === 0 && currentData.totalAnimals === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-los">
            No stay data available yet. Run the outcome scheduler to populate historical stays.
          </div>
        )}
        {outcomedData && currentData && (outcomedData.totalAnimals > 0 || currentData.totalAnimals > 0) && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <h4 className="text-sm font-medium mb-3" data-testid="text-los-outcomed-title">Outcomed Animals</h4>
              <LosBarChart data={outcomedData} title="Outcomed" testId="chart-los-outcomed" />
            </div>
            <div>
              <h4 className="text-sm font-medium mb-3" data-testid="text-los-current-title">Currently In Shelter</h4>
              <LosBarChart data={currentData} title="Current" testId="chart-los-current" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
